/**
 * Polls a turn's progress while its streamed response is unreadable.
 *
 * The completions response is SSE, which assumes every hop forwards bytes as
 * they arrive. Deployments sitting behind a hop that buffers the whole response
 * first (AWS API Gateway HTTP APIs do, with no setting to change it) show the
 * user nothing until the turn ends. The backend mirrors each typed reasoning
 * step to a short GET, and this drives the reads.
 *
 * Self-disabling by design: `shouldStop` is wired to "the stream has delivered a
 * step", which proves the stream is not buffered, so a deployment that streams
 * properly issues at most one poll and then stops. Timer functions are injected
 * so the loop is testable without racing real time.
 */

// One second: a turn emits a handful of discrete steps over its life; this
// keeps buffered-deployment users at most a beat behind each step without
// meaningfully multiplying requests (the poller stops as soon as the real
// stream proves itself).
const DEFAULT_INTERVAL_MS = 1000;
// One beat longer than a fast turn's first step, so a deployment whose stream
// works never issues a poll at all.
const DEFAULT_START_DELAY_MS = 1500;
const DEFAULT_MAX_FAILURES = 3;

/**
 * @param {object} opts
 * @param {(since: number) => Promise<{steps: Array, next: number, done: boolean}|null>} opts.fetchProgress
 * @param {(steps: Array) => void} opts.onSteps - called only with steps not seen before
 * @param {() => boolean} [opts.shouldStop] - checked before every request
 * @param {number} [opts.intervalMs]
 * @param {number} [opts.startDelayMs]
 * @param {number} [opts.maxFailures] - consecutive failures before giving up
 * @param {Function} [opts.schedule] - (fn, ms) => timerId
 * @param {Function} [opts.cancel] - (timerId) => void
 * @param {(err: Error) => void} [opts.onError]
 * @returns {{start: Function, stop: Function}}
 */
export const createProgressPoller = ({
  fetchProgress,
  onSteps,
  shouldStop = () => false,
  intervalMs = DEFAULT_INTERVAL_MS,
  startDelayMs = DEFAULT_START_DELAY_MS,
  maxFailures = DEFAULT_MAX_FAILURES,
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (id) => clearTimeout(id),
  onError = () => {},
}) => {
  let cursor = 0;
  let failures = 0;
  let timer = null;
  let stopped = false;
  let started = false;

  const stop = () => {
    stopped = true;
    if (timer !== null) {
      cancel(timer);
      timer = null;
    }
  };

  const scheduleNext = (delay) => {
    if (stopped) return;
    timer = schedule(tick, delay);
  };

  async function tick() {
    timer = null;
    if (stopped) return;
    if (shouldStop()) {
      stop();
      return;
    }

    let page;
    try {
      page = await fetchProgress(cursor);
      failures = 0;
    } catch (err) {
      failures += 1;
      onError(err);
      // A poll failing is not the turn failing — the stream is still the source
      // of truth for the answer, so retry a few times and then stay quiet
      // rather than hammering an endpoint that is clearly not answering.
      if (failures >= maxFailures) {
        stop();
        return;
      }
      scheduleNext(intervalMs);
      return;
    }

    // stop() may have been called while the request was in flight — after the
    // turn completed, say. Applying a late page would revive a dead poller and
    // push steps into a message that is already final.
    if (stopped) return;

    // A null page is an unknown turn: the poll beat the first step, or the
    // record expired. Neither is "done".
    if (page) {
      if (Array.isArray(page.steps) && page.steps.length > 0) {
        onSteps(page.steps);
      }
      if (typeof page.next === 'number' && page.next >= cursor) {
        cursor = page.next;
      }
      if (page.done) {
        stop();
        return;
      }
    }

    scheduleNext(intervalMs);
  }

  return {
    start() {
      if (started || stopped) return;
      started = true;
      scheduleNext(startDelayMs);
    },
    stop,
  };
};
